import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, ShieldCheck, XCircle, CheckCircle2, Send, UserCheck, ArrowRight, Star, Eye, MailPlus, Calendar as CalIcon, PartyPopper, TrendingUp, ImagePlus, MessageSquareQuote } from "lucide-react";
import GalleryManager from "@/components/GalleryManager";
import ReviewsManager from "@/components/ReviewsManager";

function StatusBadge({ status }) {
  const map = {
    pending: "bg-yellow-500 text-black",
    approved: "bg-[#8DE038] text-[#050806]",
    rejected: "bg-red-500/40 text-red-200",
    pending_payment: "bg-yellow-500 text-black",
    awaiting_contractor: "bg-blue-500 text-white",
    accepted: "bg-[#8DE038] text-[#050806]",
    completed: "bg-[#1F3325] text-[#8DE038]",
    cancelled: "bg-red-500/30 text-red-300",
  };
  return <span className={`px-2 py-1 text-[10px] font-display uppercase tracking-widest ${map[status] || ""}`}>{(status || "").replace(/_/g," ")}</span>;
}

function StatCard({ label, value, accent, testId }) {
  return (
    <div data-testid={testId} className={`p-6 border ${accent ? "border-[#8DE038]" : "border-[#1F3325]"} bg-[#0F1712]`}>
      <div className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">{label}</div>
      <div className="font-display text-4xl text-[#F0F4F1] mt-2">{value}</div>
    </div>
  );
}

function AssignModal({ booking, contractors, onClose, onAssign }) {
  const [cid, setCid] = useState("");
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center px-5" onClick={onClose}>
      <div onClick={(e)=>e.stopPropagation()} className="bg-[#0F1712] border border-[#1F3325] p-8 w-full max-w-md">
        <h3 className="font-display uppercase text-xl text-[#F0F4F1]">Assign job</h3>
        <p className="text-[#9CAEA3] text-sm mt-1">{booking.service} · {booking.postcode}</p>
        <select className="gl-input mt-4" value={cid} onChange={(e)=>setCid(e.target.value)} data-testid="assign-select">
          <option value="">Choose a contractor…</option>
          {contractors.filter(c=>c.contractor_status === "approved").map((c)=>(
            <option key={c.id} value={c.id}>{c.name} · {(c.service_areas || []).join(", ") || "—"} · {c.rating_count ? `${c.rating_avg}★` : "new"}</option>
          ))}
        </select>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="btn-secondary text-xs flex-1 justify-center">Cancel</button>
          <button data-testid="confirm-assign" disabled={!cid} onClick={()=>onAssign(cid)} className="btn-primary text-xs flex-1 justify-center disabled:opacity-50">Assign</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, b, a] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/contractors"), api.get("/bookings/mine"), api.get("/admin/analytics"),
      ]);
      setStats(s.data); setContractors(c.data); setBookings(b.data); setAnalytics(a.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateContractor = async (id, status) => { await api.patch(`/admin/contractors/${id}/status`, { status }); load(); };
  const broadcast = async (id) => { await api.post(`/admin/bookings/${id}/broadcast`, { note: "" }); load(); };
  const assign = async (id, cid) => {
    try { await api.post(`/admin/bookings/${id}/assign`, { contractor_id: cid }); setAssigning(null); load(); }
    catch (e) { alert(formatErr(e?.response?.data?.detail)); }
  };

  if (!user) return null;

  return (
    <div data-testid="admin-page" className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="font-display uppercase tracking-[0.3em] text-xs text-[#8DE038] mb-3">/ Admin</div>
            <h1 className="font-display uppercase tracking-tighter text-4xl md:text-6xl text-[#F0F4F1] heading-accent">Green Connect ops</h1>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            <StatCard testId="stat-customers" label="Customers" value={stats.customers}/>
            <StatCard testId="stat-contractors-approved" label="Approved Pros" value={stats.contractors_approved}/>
            <StatCard testId="stat-contractors-pending" label="Pending Pros" value={stats.contractors_pending} accent/>
            <StatCard testId="stat-bookings-open" label="Open Jobs" value={stats.bookings_awaiting} accent/>
            <StatCard testId="stat-commission" label="Commission £" value={`£${stats.commission_gbp}`}/>
          </div>
        )}

        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: "bookings", l: `Bookings (${bookings.length})` },
            { id: "contractors", l: `Contractors (${contractors.length})` },
            { id: "gallery", l: "Gallery" },
            { id: "reviews", l: "Reviews" },
            { id: "analytics", l: "Analytics" },
          ].map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={()=>setTab(t.id)} className={`px-4 py-2 border font-display uppercase tracking-widest text-xs ${tab === t.id ? "bg-[#8DE038] text-[#050806] border-[#8DE038]" : "border-[#1F3325] text-[#F0F4F1]"}`}>{t.l}</button>
          ))}
        </div>

        {loading && <div className="text-[#9CAEA3] flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</div>}

        {tab === "bookings" && (
          <div className="space-y-3">
            {bookings.length === 0 && <div className="text-[#9CAEA3] border border-dashed border-[#1F3325] p-8 text-center">No bookings.</div>}
            {bookings.map((b) => (
              <div key={b.id} data-testid={`admin-booking-${b.id}`} className="border border-[#1F3325] bg-[#0F1712] p-5 grid md:grid-cols-12 gap-4">
                <div className="md:col-span-3">
                  <div className="font-display uppercase text-xs text-[#8DE038] tracking-widest">{b.preferred_date} · {b.preferred_time}</div>
                  <div className="font-display uppercase text-lg text-[#F0F4F1] mt-1">{b.service}</div>
                  <div className="text-[#9CAEA3] text-sm">£{b.estimated_value_gbp} · fee £{b.platform_fee_gbp}</div>
                </div>
                <div className="md:col-span-5 text-sm text-[#C8D4CB] space-y-1">
                  <div>👤 {b.customer_name} · {b.customer_email}</div>
                  <div>📍 {b.address}, {b.postcode} <span className="text-[#8DE038] ml-2">({b.postcode_area})</span></div>
                  {b.contractor_name && <div>🔨 Pro: <span className="text-[#F0F4F1]">{b.contractor_name}</span></div>}
                  {b.notes && <div className="text-[#9CAEA3] italic">"{b.notes}"</div>}
                </div>
                <div className="md:col-span-2"><StatusBadge status={b.status}/>{b.broadcast && <div className="mt-1 text-[10px] font-display uppercase tracking-widest text-blue-400">Broadcast</div>}</div>
                <div className="md:col-span-2 flex flex-col gap-2">
                  {(b.status === "awaiting_contractor" || b.status === "pending_payment") && !b.contractor_id && (
                    <>
                      <button onClick={()=>setAssigning(b)} data-testid={`assign-${b.id}`} className="btn-primary text-[10px] justify-center py-2"><UserCheck size={12}/> Assign</button>
                      {!b.broadcast && <button onClick={()=>broadcast(b.id)} data-testid={`broadcast-${b.id}`} className="btn-secondary text-[10px] justify-center py-2"><Send size={12}/> Broadcast</button>}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "contractors" && (
          <div className="space-y-3">
            {contractors.length === 0 && <div className="text-[#9CAEA3] border border-dashed border-[#1F3325] p-8 text-center">No contractors yet.</div>}
            {contractors.map((c) => (
              <div key={c.id} data-testid={`contractor-${c.id}`} className="border border-[#1F3325] bg-[#0F1712] p-5 grid md:grid-cols-12 gap-4">
                <div className="md:col-span-3 flex items-start gap-3">
                  {c.avatar_url ? <img src={c.avatar_url} alt="" className="w-14 h-14 object-cover border border-[#1F3325]"/> : <div className="w-14 h-14 border border-[#1F3325] flex items-center justify-center text-[#6B7C71] text-xs">No pic</div>}
                  <div>
                    <div className="font-display uppercase text-lg text-[#F0F4F1]">{c.name}</div>
                    <div className="text-[#9CAEA3] text-sm">{c.email}</div>
                    {c.rating_count > 0 && <div className="text-[#8DE038] text-xs flex items-center gap-1"><Star size={10} fill="#8DE038"/> {c.rating_avg} ({c.rating_count})</div>}
                  </div>
                </div>
                <div className="md:col-span-4 text-sm text-[#C8D4CB] space-y-1">
                  <div>📍 Areas: <span className="text-[#F0F4F1]">{(c.service_areas || []).join(", ") || "—"}</span></div>
                  <div>🛠 Skills: <span className="text-[#F0F4F1]">{(c.skills || []).join(", ") || "—"}</span></div>
                  <div className="flex gap-2 text-xs">
                    <span className={c.id_doc_url ? "text-[#8DE038]" : "text-[#6B7C71]"}>{c.id_doc_url ? "✓ ID" : "✗ ID"}</span>
                    <span className={c.insurance_doc_url ? "text-[#8DE038]" : "text-[#6B7C71]"}>{c.insurance_doc_url ? "✓ Insurance" : "✗ Insurance"}</span>
                  </div>
                </div>
                <div className="md:col-span-2"><StatusBadge status={c.contractor_status}/></div>
                <div className="md:col-span-3 flex flex-col gap-2">
                  {c.contractor_status !== "approved" && (<button onClick={()=>updateContractor(c.id, "approved")} data-testid={`approve-${c.id}`} className="btn-primary text-[10px] justify-center py-2"><CheckCircle2 size={12}/> Approve</button>)}
                  {c.contractor_status !== "rejected" && (<button onClick={()=>updateContractor(c.id, "rejected")} data-testid={`reject-${c.id}`} className="btn-secondary text-[10px] justify-center py-2"><XCircle size={12}/> Reject</button>)}
                  {c.id_doc_url && <a href={c.id_doc_url} target="_blank" rel="noreferrer" className="text-[10px] font-display uppercase tracking-widest text-[#8DE038]">View ID →</a>}
                  {c.insurance_doc_url && <a href={c.insurance_doc_url} target="_blank" rel="noreferrer" className="text-[10px] font-display uppercase tracking-widest text-[#8DE038]">View insurance →</a>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "gallery" && (<GalleryManager />)}

        {tab === "reviews" && (<ReviewsManager />)}

        {tab === "analytics" && analytics && (
          <div data-testid="admin-analytics" className="space-y-6">
            <div>
              <div className="font-display uppercase tracking-widest text-xs text-[#9CAEA3] mb-4">Conversion funnel · all-time</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <FunnelStep testId="funnel-visits" icon={Eye} label="Visits" value={analytics.visits.total} sub={`${analytics.visits.week} this week`} pctNext={analytics.conversion.visit_to_lead}/>
                <FunnelStep testId="funnel-leads" icon={MailPlus} label="Leads" value={analytics.leads.total} sub={`${analytics.leads.week} this week`} pctNext={analytics.conversion.lead_to_booking}/>
                <FunnelStep testId="funnel-bookings" icon={CalIcon} label="Bookings" value={analytics.bookings.total} sub={`${analytics.bookings.week} this week`} pctNext={analytics.conversion.booking_to_paid}/>
                <FunnelStep testId="funnel-paid" icon={CheckCircle2} label="Paid" value={analytics.bookings.paid} sub="Customer paid the fee" pctNext={analytics.conversion.paid_to_completed}/>
                <FunnelStep testId="funnel-completed" icon={PartyPopper} label="Completed" value={analytics.bookings.completed} sub={`£${analytics.revenue_gbp} commission`}/>
              </div>
            </div>

            <VisitsSparkline series={analytics.visits.series_14d}/>

            <div className="grid md:grid-cols-3 gap-3">
              <div className="border border-[#1F3325] bg-[#0F1712] p-6">
                <div className="font-display uppercase tracking-widest text-xs text-[#8DE038]">Leads · last 30 days</div>
                <div className="font-display text-4xl text-[#F0F4F1] mt-2">{analytics.leads.month}</div>
                <div className="text-xs text-[#9CAEA3] mt-1">vs {analytics.leads.week} this week</div>
              </div>
              <div className="border border-[#1F3325] bg-[#0F1712] p-6">
                <div className="font-display uppercase tracking-widest text-xs text-[#8DE038]">Discount captures</div>
                <div className="font-display text-4xl text-[#F0F4F1] mt-2">{analytics.discount_captures}</div>
                <div className="text-xs text-[#9CAEA3] mt-1">Exit-popup emails captured</div>
              </div>
              <div className="border border-[#8DE038] bg-[#0F1712] p-6">
                <div className="font-display uppercase tracking-widest text-xs text-[#8DE038]">Revenue · all time</div>
                <div className="font-display text-4xl text-[#F0F4F1] mt-2">£{analytics.revenue_gbp}</div>
                <div className="text-xs text-[#9CAEA3] mt-1">Platform fee collected</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {assigning && <AssignModal booking={assigning} contractors={contractors} onClose={()=>setAssigning(null)} onAssign={(cid)=>assign(assigning.id, cid)}/>}
    </div>
  );
}

function FunnelStep({ icon: I, label, value, sub, pctNext, testId }) {
  return (
    <div data-testid={testId} className="relative border border-[#1F3325] bg-[#0F1712] p-6 flex flex-col">
      <div className="flex items-center gap-2 text-[#8DE038]"><I size={18}/><span className="font-display uppercase tracking-widest text-xs">{label}</span></div>
      <div className="font-display text-5xl text-[#F0F4F1] mt-3 leading-none">{value}</div>
      {sub && <div className="text-xs text-[#9CAEA3] mt-2 uppercase tracking-widest">{sub}</div>}
      {pctNext != null && (
        <div className="mt-4 pt-4 border-t border-[#1F3325] text-xs text-[#9CAEA3] flex items-center gap-1"><TrendingUp size={12} className="text-[#8DE038]"/> {pctNext}% convert →</div>
      )}
    </div>
  );
}

function VisitsSparkline({ series }) {
  if (!series || series.length === 0) return null;
  const max = Math.max(...series.map((d) => d.count), 1);
  return (
    <div data-testid="visits-sparkline" className="border border-[#1F3325] bg-[#0F1712] p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display uppercase tracking-widest text-xs text-[#8DE038]">Visits — last 14 days</div>
        <div className="text-xs text-[#9CAEA3]">Peak {max}/day</div>
      </div>
      <div className="flex items-end gap-1 h-32">
        {series.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
            <div className="text-[10px] text-[#6B7C71] opacity-0 group-hover:opacity-100">{d.count}</div>
            <div className="w-full bg-[#8DE038]/70 group-hover:bg-[#8DE038] transition-colors" style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? "2px" : "1px" }} title={`${d.date}: ${d.count}`}></div>
            <div className="text-[9px] text-[#6B7C71] -rotate-45 origin-top-left whitespace-nowrap mt-1">{d.date.slice(5)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
